import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ArticleCard from '@/components/blog/ArticleCard';
import ArticleCardList from '@/components/blog/ArticleCardList';
import CustomHeading from '@/components/blog/CustomHeading';
import { Pagination } from '@/components/categ/Pagination';
import Tag from '@/components/common/Tag';
import TagList from '@/components/common/TagList';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { POSITION_TYPE } from '@/constants/blog';
import { PAGE_TYPE } from '@/constants/datalayer';
import { PAGES } from '@/constants/pages';
import { useAppDispatch, useAppSelector } from '@/redux';
import { setBlogResults } from '@/redux/slices/blog.slice';
import { useServices } from '@/services/useServices';
import {
	getArticleCardTypeAccordingToIndex,
	getBlogImageSrc,
	getCategPageTags,
	sliceListIntoGroups,
	updateUrlWithParams,
} from '@/utils/blogUtils';
import { scrollToElement } from '@/utils/scroll';
import styles from './CategBlogPage.module.scss';

export const CategBlogPage = ({ tagSelected, articleCateg, topArticle, articlesResult, tags }) => {
	const dispatch = useAppDispatch();
	const { t } = useTranslation([NS.COMMON, NS.BLOG]);
	const { locale, storeId } = useAppSelector(({ root }) => root.storeConf);
	const { datalayerService, seoService } = useServices();
	const { isMobile, isDesktop } = useScreenSizeContext();
	const [articlesResultSplit, setArticlesResultSplit] = useState(sliceListIntoGroups([6, 2], articlesResult.content));
	const [pageableData, setPageableData] = useState(articlesResult);
	const [selectedTagId, setSelectedTagId] = useState(tagSelected?.tagIdentifier || '');
	const [scrollTo, setScrollTo] = useState(false);
	const router = useRouter();
	const nodeRef = useRef<HTMLDivElement>(null);
	const articleCategId = articleCateg?.id;
	const topArticleAggregationId = topArticle?.id;
	const currentPageUrl = `${PAGES.BLOG}/${articleCateg.content[0].urlKeyword}`;
	const categTags = getCategPageTags({ tags }, t);
	const { imageName, imageAlt, title, shortDesc, publishedAt, urlKeyword } = topArticle.articles[0];

	const customization = {
		customClassName: { articleCardList: styles['articles'] },
		headingLevel: { articleCard: 2 },
	};

	useEffect(() => {
		datalayerService.pushPageName(PAGE_TYPE.BLOG_CATEG, PAGE_TYPE.BLOG_CATEG);
	}, []);

	const handleClick = (selectedTagId) => {
		setSelectedTagId(selectedTagId);
		setScrollTo(true);
	};

	const updatePaginationMetaTag = (blogResults) => {
		dispatch(setBlogResults({ blogResults }));
	};

	useEffect(() => {
		if (scrollTo) {
			scrollToElement(nodeRef, { behavior: 'instant', block: 'start', inline: 'start' });
			setScrollTo(false);
		}
	}, [scrollTo]);

	useEffect(() => {
		setArticlesResultSplit(sliceListIntoGroups([6, 2], articlesResult.content));
		updatePaginationMetaTag(articlesResult);
		setPageableData(articlesResult);
	}, [articlesResult, locale, isMobile, isDesktop]);

	const tagsClassName = [styles['tags-section'], isDesktop && styles['tags-section-desktop']].filter(Boolean).join(' ');

	const filterByTagOrPage = async ({ tagId = '', page = 1 }) => {
		const filteredBlogData: any = await seoService.getFilteredCategBlogData({
			storeId,
			articleCategId,
			topArticleAggregationId,
			page,
			tagFilter: tagId,
		});

		setPageableData(filteredBlogData);
		updatePaginationMetaTag(filteredBlogData);
		setArticlesResultSplit(sliceListIntoGroups([6, 2], filteredBlogData.content));
		setSelectedTagId(tagId);
		updateUrlWithParams({ router, currentPageUrl, page, filter: tagId });
		setScrollTo(true);
	};

	const filterByPage = (page) => {
		filterByTagOrPage({ tagId: selectedTagId, page });
	};

	return (
		<>
			{imageName && (
				<Head>
					<link rel="preload" href={getBlogImageSrc(imageName, isMobile)} as="image" />
				</Head>
			)}
			<div data-test-id="blog-categ-page" data-rbf="blog-categ-page">
				<CustomHeading
					title={articleCateg.content[0].title}
					headingLevel={1}
					labelPosition={POSITION_TYPE.CENTER}
					customClassName={{ label: styles['title'] }}
				/>

				<div className={styles['top-article-section']}>
					<ArticleCard
						id={topArticle.id}
						imageSrc={getBlogImageSrc(imageName, isMobile)}
						imageAlt={imageAlt}
						tags={topArticle.tags}
						title={title}
						shortDesc={shortDesc}
						publishedAt={publishedAt}
						urlKeyword={urlKeyword}
						customization={{ headingLevel: 2 }}
						isTopArticle={true}
					/>
				</div>

				{articlesResultSplit.length > 0 && (
					<div ref={nodeRef}>
						<TagList t={t} displayScrollBar={!isDesktop} customClassName={tagsClassName}>
							{categTags.map((tag) => (
								<div onClick={() => handleClick(selectedTagId)} key={tag.id}>
									<Tag
										key={tag.id}
										isActive={tag.id === selectedTagId}
										isActiveWithBackground={tag.id === selectedTagId}
										clickable
										onClose={() => filterByTagOrPage({ tagId: tag.id })}
									>
										{tag.title}
									</Tag>
								</div>
							))}
						</TagList>
					</div>
				)}

				{articlesResultSplit.map((articles, index) => (
					<ArticleCardList
						key={index}
						articles={articles}
						articleCardType={getArticleCardTypeAccordingToIndex(index)}
						customization={customization}
					/>
				))}

				{pageableData?.totalPages > 1 ? (
					<Pagination
						t={t}
						categ={{ urlKeyword: currentPageUrl }}
						page={pageableData.pageable.pageNumber + 1}
						nbTotalRows={pageableData.totalElements}
						pageSize={pageableData.pageable.pageSize}
						onChange={filterByPage}
						isMobile={isMobile}
						customClassName={styles['pagination-section']}
					/>
				) : (
					<></>
				)}
			</div>
		</>
	);
};

CategBlogPage.propTypes = {
	tagSelected: PropTypes.bool,
	articleCateg: PropTypes.shape({
		id: PropTypes.string,
		content: PropTypes.array,
	}).isRequired,
	topArticle: PropTypes.shape({
		id: PropTypes.string,
		articles: PropTypes.array,
		tags: PropTypes.array,
	}).isRequired,
	articlesResult: PropTypes.shape({
		content: PropTypes.array,
	}).isRequired,
	tags: PropTypes.array,
};

export default CategBlogPage;
